import { Link } from 'react-router-dom';
import type { Topic } from '@/types/content';
import { formatHeat } from '@/utils/format';

interface TopicCardProps {
  topic: Topic;
  variant?: 'vertical' | 'horizontal' | 'minimal';
}

const CategoryTag = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-900">
    <i className="ri-bookmark-3-line text-secondary-700" aria-hidden="true" />
    {label}
  </span>
);

export default function TopicCard({ topic, variant = 'vertical' }: TopicCardProps) {
  const href = `/topic/${topic.id}`;

  if (variant === 'horizontal') {
    return (
      <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300">
        <Link
          to={href}
          className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-40"
          aria-label={topic.title}
        >
          <img
            src={topic.cover}
            alt={topic.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          {topic.isBreaking && (
            <span className="absolute left-2 top-2 rounded-sm bg-primary-500 px-1.5 py-0.5 text-[10px] font-medium text-background-50">
              最新
            </span>
          )}
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <CategoryTag label={topic.categoryName} />
          <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950">
            <Link to={href} className="transition-colors hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 hidden text-xs leading-relaxed text-foreground-600 sm:block">
            {topic.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 text-[11px] text-foreground-600">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              {topic.updatedAt}
            </span>
            <span className="flex items-center gap-1 text-primary-700">
              <i className="ri-fire-line" aria-hidden="true" />
              {formatHeat(topic.heat)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'minimal') {
    return (
      <article className="flex items-start gap-3 border-b border-background-200 pb-3 last:border-b-0 last:pb-0">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xs font-bold text-accent-800">
          <i className="ri-fire-line" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900">
            <Link to={href} className="transition-colors hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-1 flex items-center gap-2 text-[11px] text-foreground-600">
            <span>{topic.categoryName}</span>
            <span className="text-background-400">|</span>
            <span>{topic.updatedAt.slice(5, 16)}</span>
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={href} className="relative block h-44 w-full overflow-hidden bg-background-200" aria-label={topic.title}>
        <img
          src={topic.cover}
          alt={topic.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute right-2 top-2 rounded-md bg-background-50/90 px-2 py-0.5 text-[10px] font-medium text-foreground-800">
          {topic.categoryName}
        </span>
        {topic.isBreaking && (
          <span className="absolute left-2 top-2 rounded-md bg-primary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
            最新
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={href} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4 text-[11px] text-foreground-600">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line" aria-hidden="true" />
            {topic.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-primary-700">
              <i className="ri-fire-line" aria-hidden="true" />
              {formatHeat(topic.heat)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {topic.comments}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}